import { makeApiRequest, tsToDate, fetchPSXSymbols } from "./helpers.js";
import { subscribeOnStream, unsubscribeFromStream } from "./streaming.js";

// ─── Cache ────────────────────────────────────────────────────────────────────
const lastBarsCache  = new Map();
let   symbolsCache   = null;        // resolved once, then reused

async function getPSXSymbols() {
  if (!symbolsCache) symbolsCache = await fetchPSXSymbols();
  return symbolsCache;
}

// ─── Configuration ────────────────────────────────────────────────────────────
const configurationData = {
  supported_resolutions: ["1D", "1W", "1M"],
  exchanges: [
    { value: "PSX", name: "PSX", desc: "Pakistan Stock Exchange" },
  ],
  symbols_types: [
    { name: "Stock", value: "stock" },
  ],
};

// ─── Datafeed ─────────────────────────────────────────────────────────────────
export default {

  onReady: (callback) => {
    console.log("[onReady]");
    setTimeout(() => callback(configurationData));
  },

  // Called when the user types in the built-in symbol search box
  searchSymbols: async (userInput, exchange, _symbolType, onResultReadyCallback) => {
    console.log("[searchSymbols]", userInput);
    const list = await getPSXSymbols();
    const q = userInput.toLowerCase();
    const filtered = list
      .filter(s =>
        (exchange === "" || exchange === "PSX") &&
        (s.code?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q))
      )
      .map(s => ({
        symbol:      s.code,
        full_name:   `PSX:${s.code}`,
        description: s.name || s.code,
        exchange:    "PSX",
        ticker:      s.code,
        type:        "stock",
      }));
    onResultReadyCallback(filtered);
  },

  // Called when a symbol is selected / the chart first loads
  resolveSymbol: async (symbolName, onSymbolResolvedCallback, onResolveErrorCallback) => {
    console.log("[resolveSymbol]", symbolName);

    // strip exchange prefix if present (e.g. "PSX:PPL" → "PPL")
    const code = symbolName.includes(":") ? symbolName.split(":")[1] : symbolName;

    const list = await getPSXSymbols();
    const sym  = list.find(s => s.code?.toUpperCase() === code.toUpperCase());

    if (!sym) {
      console.warn("[resolveSymbol] not found:", code);
      onResolveErrorCallback("Symbol not found");
      return;
    }

    const symbolInfo = {
      ticker:      sym.code,
      name:        sym.code,
      full_name:   `PSX:${sym.code}`,
      description: sym.name || sym.code,
      type:        "stock",
      session:     "0415-1030",   // PSX 09:15–15:30 PKT = 04:15–10:30 UTC
      timezone:    "Asia/Karachi",
      exchange:    "PSX",
      listed_exchange: "PSX",
      minmov:      1,
      pricescale:  100,
      has_intraday:           false,
      has_daily:              true,
      has_weekly_and_monthly: false,   // let the library aggregate from daily
      supported_resolutions:  configurationData.supported_resolutions,
      volume_precision: 0,
      data_status: "endofday",
    };

    console.log("[resolveSymbol] resolved:", sym.code);
    onSymbolResolvedCallback(symbolInfo);
  },

  // Fetch historical OHLCV bars from the chart API
  getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
    const { from, to, firstDataRequest } = periodParams;
    const code = symbolInfo.ticker || symbolInfo.name;
    console.log("[getBars]", code, resolution, tsToDate(from), "→", tsToDate(to));

    try {
      const fromDate = tsToDate(from);
      const toDate   = tsToDate(to);

      const data = await makeApiRequest(
        `api/chart/${code}?from_date=${fromDate}&to_date=${toDate}&is_index=`
      );

      const candles = data.candles || [];

      if (candles.length === 0) {
        onHistoryCallback([], { noData: true });
        return;
      }

      const bars = candles
        .map(c => ({
          time:   new Date(c.timestamp).getTime(),   // ms
          open:   parseFloat(c.open_price),
          high:   parseFloat(c.high_price),
          low:    parseFloat(c.low_price),
          close:  parseFloat(c.close_price),
          volume: parseInt(c.volume) || 0,
        }))
        .filter(b => !isNaN(b.open) && !isNaN(b.close))
        .sort((a, b) => a.time - b.time);

      if (firstDataRequest && bars.length > 0) {
        lastBarsCache.set(code, { ...bars[bars.length - 1] });
      }

      console.log(`[getBars] returning ${bars.length} bars for ${code}`);
      onHistoryCallback(bars, { noData: bars.length === 0 });

    } catch (err) {
      console.error("[getBars] error:", err);
      onErrorCallback(err.message);
    }
  },

  subscribeBars: (symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) => {
    console.log("[subscribeBars]", subscriberUID);
    subscribeOnStream(
      symbolInfo, resolution, onRealtimeCallback,
      subscriberUID, onResetCacheNeededCallback,
      lastBarsCache.get(symbolInfo.ticker || symbolInfo.name)
    );
  },

  unsubscribeBars: (subscriberUID) => {
    console.log("[unsubscribeBars]", subscriberUID);
    unsubscribeFromStream(subscriberUID);
  },
};
